import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import styles from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderNavigation.module.css';
import { OnboardingRoadItem } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import { useIssuerGetInvestorEditByIdQuery } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Badge, Spinner, Tooltip, TooltipNew } from 'ui';
import { DoneIcon } from 'ui/icons';
import { InvestorEditsStatus } from 'api';

export interface OnboardingHolderNavigationProps {
  roads?: OnboardingRoadItem[];
}

export const OnboardingHolderNavigation = ({ roads }: OnboardingHolderNavigationProps) => {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const step = params.get('step');
  const { id = 0 } = useParams();
  const { data: dataHolder, isLoading } = useIssuerGetInvestorEditByIdQuery(
    +id,
    id === 0 || isNaN(+id),
  );
  const isUpdate = useMemo(
    () =>
      state?.action === 'update' ||
      !!dataHolder?.data?.old_data?.shareholder_id ||
      !!dataHolder?.data?.new_data?.entity_data?.document_files?.length,
    [dataHolder?.data, state?.action],
  );
  const { navName: currentNavName } = roads?.[step && !isNaN(+step) ? +step : 0] ?? {};

  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const getSubStepRoad = useCallback(
    (step: number, subStep: number) => +(params.get(`sub_step_road${step}_${subStep}`) ?? 0),
    [params],
  );
  const delSubStep = useCallback((step: number) => params.delete(`sub_step${step}`), [params]);
  const delSubStepRoad = useCallback(
    (step: number, subStep: number) => params.delete(`sub_step_road${step}_${subStep}`),
    [params],
  );

  const getPercentWithSubRoad = useCallback(
    (shareholders: ShareholderDetails[], step: number, allSubStep = 2) => {
      const filteredShareholders = shareholders?.filter(item => !item.same_as_index);
      if (!filteredShareholders || filteredShareholders?.length === 0) {
        return 100;
      }
      const countShareholders = (filteredShareholders?.length ?? 1) * allSubStep;
      const subStep = getSubStep(step);
      const subStepRoad = getSubStepRoad(step, subStep);
      const percent = ((subStep * allSubStep + (subStepRoad + 1)) / countShareholders) * 100;
      return percent;
    },
    [getSubStep, getSubStepRoad],
  );

  const goToPage = useCallback(
    (newStep: number) => {
      const currentStep = params.get('step') ?? '0';
      params.set('step', String(newStep));
      const subStep = getSubStep(+currentStep);
      delSubStepRoad(+currentStep, subStep);
      delSubStep(+currentStep);
      delSubStepRoad(newStep, 0);
      delSubStep(newStep);
      setParams(params, {
        state,
        replace: false,
      });
    },
    [delSubStep, delSubStepRoad, getSubStep, params, setParams, state],
  );

  if (!currentNavName) {
    return <></>;
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-2 m-0 self-start w-full h-full min-h-[300px] select-none',
        'font-[Inter] text-[16px] leading-6 overflow-y-auto max-h-full pr-1',
        styles.scroll,
      )}
    >
      {isLoading && id !== 0 ? (
        <div className="w-full flex items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      ) : (
        roads?.map((item, i, all) => {
          const checkAllCompleted = !roads?.some((itemA, j) => {
            const result = itemA.getNavProgress({
              user_data: dataHolder?.data?.new_data?.user_data,
              data: dataHolder?.data?.new_data?.entity_data,
              shareholder_id: dataHolder?.data?.shareholder_id,
              getSubStep,
              getPercentWithSubRoad,
              step: j,
            });

            return j < 3 || j > roads?.length - 2 ? false : !result.completed;
          });

          const { getNavProgress } = roads?.[i] ?? {};
          const currentProgress = getNavProgress({
            user_data: dataHolder?.data?.new_data?.user_data,
            data: dataHolder?.data?.new_data?.entity_data,
            shareholder_id: dataHolder?.data?.shareholder_id,
            getSubStep,
            getPercentWithSubRoad,
            step: i,
          });

          const disabledHolder = i === 0 && currentProgress.completed;
          const disabledType = i === 2 && currentProgress.completed;
          const disabledCountry = i === 1 && currentProgress.completed;
          const disabled =
            (i !== 0 && id === 0) ||
            (i === 0 && currentProgress.completed) ||
            (i === 1 && currentProgress.completed) ||
            (i === 2 && currentProgress.completed);
          const isConfirmationTab = i === all.length - 1;
          const isInProgress =
            dataHolder?.data?.status === InvestorEditsStatus.InProgress && checkAllCompleted;
          const hasMissingInfo = !currentProgress.completed && isUpdate && !isConfirmationTab;

          return (
            <div
              className={classNames('flex gap-3 group relative', {
                'min-h-9 h-9': !!item.navName,
                'h-0 !hidden': !item.navName,
                'cursor-pointer': !disabled,
                hidden: isUpdate && i <= 2 && (disabledType || disabledCountry || disabledHolder),
              })}
              key={i}
              onClick={() => !disabled && goToPage(i)}
            >
              <div className={classNames('relative w-0.5')}>
                <div
                  className="absolute top-0 left-0 bg-blue-700 w-0.5 h-full"
                  style={{
                    height: currentNavName === item.navName ? `${currentProgress.percent}%` : '0%',
                  }}
                />
              </div>
              <TooltipNew
                text={
                  disabledHolder
                    ? 'The holder has already been created'
                    : disabledType
                    ? 'The type has already been selected'
                    : disabledCountry
                    ? 'This step cannot be filled again'
                    : 'The first steps need to be completed'
                }
                disabled={!disabled}
              >
                <Tooltip
                  relativeBlock={false}
                  delayTimeOpen={700}
                  position="up"
                  className="flex items-center"
                  positionHorizontal="center"
                  button={
                    <div className="flex items-center gap-3">
                      <span
                        className={classNames(
                          'block text-center leading-9',
                          'max-w-[305px] min-w-0',
                          'overflow-hidden text-ellipsis whitespace-nowrap',
                          {
                            'text-black': currentNavName === item.navName && !disabled,
                            'text-gray-500': currentNavName !== item.navName && !disabled,
                            // [styles.jumpAnimation]: isConfirmationTab && isInProgress,
                            // 'shadow-[inset_0px_-7px_5px_-5px_#0000ff45]':
                            //   isConfirmationTab && isInProgress,
                            'text-gray-200': disabled,
                            'cursor-help': item.navName.length >= 45 || disabled,
                            'group-hover:underline': !disabled,
                          },
                        )}
                      >
                        <span>{item.navName}</span>
                      </span>
                      {currentProgress.completed && (
                        <img
                          src={DoneIcon}
                          alt="Completed"
                          className="h-5 w-5 min-h-5 min-w-5 shrink-0"
                        />
                      )}
                      {hasMissingInfo && (
                        <Badge containerClass="bg-red-400 text-white text-xs font-[500] font-[Inter]">
                          Missing Info
                        </Badge>
                      )}
                      {item.navName === 'Confirmation' && isConfirmationTab && isInProgress && (
                        <Badge containerClass="bg-[#0369A1] text-white text-xs font-[500] font-[Inter]">
                          Pending changes
                        </Badge>
                      )}
                    </div>
                  }
                  disabled={item.navName.length < 45}
                >
                  <span className="">{item.navName}</span>
                </Tooltip>
              </TooltipNew>
            </div>
          );
        })
      )}
    </div>
  );
};
